<template>
  <div :class="`cp-utilities-generic_modal-wrapper ${isModalCurrentlyOpen ? 'open' : 'closed'}`">
    <div class="cp-utilities-generic_modal-overlay"
         v-if="renderOverlay"
         :style="`backdrop-filter:${backgroundBlur};`"
         @click="closeModal">
      <svg class="cp-utilities-generic_modal-overlay-static" xmlns="http://www.w3.org/2000/svg">
        <filter :id="getMyId">
          <feTurbulence type="fractalNoise" baseFrequency="0.68" numOctaves="1" stitchTiles="stitch" />
        </filter>
        <rect width="100%" height="100%" :filter="`url(#${getMyId})`" />
      </svg>
    </div>

    <div class="cp-utilities-generic_modal"
         :style="`position:${position}; width:${width}; height:${height}; max-height: ${maxHeight};
          max-width: ${maxWidth}; overflow: ${overflow}; gap: ${gap}; border-radius: ${borderRadius}; border: ${border};`"
    >
      <button v-if="closeBtnPosition === 'out'"
        :style="`color: ${closeBtnColor}`"
        class="cp-utilities-generic_modal-overlay-close out"
        type="button"
        aria-label="Close"
        @click="closeModal"
      >
        <span v-html="getExitModalIcon"></span>
      </button>
      <div v-if="hasHeader" class="cp-utilities-generic_modal-header">
        <slot name="header" />
      </div>
      <div v-if="uniqueId === 'afterAddToCartModal'" class="cp-utilities-generic_modal-suggestion-text">
        {{ t('after.add.to.card.suggestion.title') }}
      </div>
      <div class="cp-utilities-generic_modal-body">
        <slot />
      </div>
      <div v-if="hasFooter" class="col-12 cp-utilities-generic_modal-footer p-0">
        <slot name="footer" />
      </div>
      <button v-if="closeBtnPosition === 'in'"
              :style="`color: ${closeBtnColor}`"
              class="cp-utilities-generic_modal-overlay-close in"
              type="button"
              aria-label="Close"
              @click="closeModal"
      >
        <span v-html="getExitModalIcon"></span>
      </button>
    </div>
  </div>
</template>

<script>

export default {
  name: 'GenericModal',
  props: {
    renderOverlay: {
      type: Boolean,
      required: false,
      default: true
    },
    shouldModalStartInOpenState: {
      type: Boolean,
      required: false,
      default: false
    },
    uniqueId: {
      type: String,
      required: true
    },
    closeBtnColor: {
      type: String,
      required: false,
      default: '#9A9A9B'
    },
    closeBtnPosition: {
      type: String,
      required: false,
      default: 'out',
      validator: (value) => ['in', 'out'].includes(value)
    },
    hasHeader: {
      type: Boolean,
      required: false,
      default: true
    },
    hasFooter: {
      type: Boolean,
      required: false,
      default: true
    },
    openDispatchEvent: {
      type: String,
      required: false
    },
    closeDispatchEvent: {
      type: String,
      required: false
    },
    width: {
      type: String,
      required: false,
      default: '100%'
    },
    height: {
      type: String,
      required: false,
      default: '100%'
    },
    maxWidth: {
      type: String,
      required: false,
      default: '1190px'
    },
    maxHeight: {
      type: String,
      required: false,
      default: '680px'
    },
    overflow: {
      type: String,
      required: false,
      default: 'unset'
    },
    gap: {
      type: String,
      required: false,
      default: 'unset'
    },
    modalOpenTriggerHandlerId: {
      type: String,
      required: false,
      default: 'modal-open'
    },
    modalCloseTriggerHandlerId: {
      type: String,
      required: false,
      default: 'modal-close'
    },
    modalOpenedTriggerHandlerId: {
      type: String,
      required: false,
      default: 'modal-opened'
    },
    modalClosedTriggerHandlerId: {
      type: String,
      required: false,
      default: 'modal-closed'
    },
    exitModalIconClass: {
      type: String,
      required: false,
      default: 'lni lni-close'
    },
    exitModalIconFontSize: {
      type: String,
      required: false,
      default: '15px'
    },
    position: {
      type: String,
      default: 'fixed'
    },
    backgroundBlur: {
      type: String,
      required: false,
      default: 'blur(1rem)'
    },
    borderRadius: {
      type: String,
      required: false,
      default: '10px'
    },
    border: {
      type: String,
      required: false,
      default: 'none'
    }
  },
  data () {
    return {
      isModalCurrentlyOpen: false
    }
  },
  computed: {
    getMyId () {
      return this.uniqueId
    },
    getExitModalIcon () {
      return `<i class="${this.exitModalIconClass}" style="font-size: ${this.exitModalIconFontSize}"></i>`
    },
    pointerEventsOverlay () {
      return this.renderOverlay ? 'auto' : 'none'
    }
  },
  mounted () {
    this.isModalCurrentlyOpen = this.shouldModalStartInOpenState;
    // Add event listener for opening the modal when a button with class cp-btn is clicked
    const buttons = document.querySelectorAll('.cp-btn');
    buttons.forEach(button => {
      button.addEventListener('click', this.addModalOpenClass);
    });
  },
  destroyed() {
    // Remove event listeners when the component is destroyed
    const buttons = document.querySelectorAll('.cp-btn');
    buttons.forEach(button => {
      button.removeEventListener('click', this.addModalOpenClass);
    });
  },
  created () {
    this.$on(this.modalOpenTriggerHandlerId, this.openModal)
    this.$on(this.modalCloseTriggerHandlerId, this.closeModal)

    // TODO: @deprecate these in the future & fire events using $refs instead of $root
    this.$root.$on(this.modalOpenTriggerHandlerId, this.openModal)
    this.$root.$on(this.modalCloseTriggerHandlerId, this.closeModal)
  },
  methods: {
    async openModal () {
      this.isModalCurrentlyOpen = true
      this.addModalOpenClass();
      if (this.openDispatchEvent !== undefined) {
        await this.$store.dispatch(this.openDispatchEvent)
      }
      this.$emit(this.modalOpenedTriggerHandlerId)
    },
    async closeModal () {
      this.isModalCurrentlyOpen = false
      this.removeModalOpenClass();
      if (this.closeDispatchEvent !== undefined) {
        await this.$store.dispatch(this.closeDispatchEvent)
      }
      this.$emit(this.modalClosedTriggerHandlerId)
    },
    addModalOpenClass() {
      const pageHeader = document.querySelector('.page-header.navbar.navbar-fixed-top');
      if (pageHeader) {
        pageHeader.classList.add('open-service-modal');
      }
    },
    removeModalOpenClass() {
      const pageHeader = document.querySelector('.page-header.navbar.navbar-fixed-top');
      if (pageHeader) {
        pageHeader.classList.remove('open-service-modal');
      }
    }
  }
}
</script>

<style lang="scss">

$transitional-profile-1: all .2s ease-out;

.cp-utilities-generic_modal {
  visibility: hidden;
  content-visibility: hidden;
  z-index: 9002;

  &-wrapper {
    position: fixed;
    top:30px;
    left: 0;
    right:0;
    margin:0 auto;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: $transitional-profile-1;
    pointer-events: none;
    user-select: none;
    display: grid;
    align-items: flex-start;
    align-content: center;
    justify-items: center;
    justify-content: center;
    z-index: 9000;
    &.open {
      opacity: 1;
      pointer-events: v-bind(pointerEventsOverlay);
      user-select: initial;

      .cp-utilities-generic_modal {
        pointer-events: all;
        visibility: visible;
        content-visibility: visible;
        display: grid;
        align-items: center;
        width: 100%;
        height: 100%;
        max-height: 680px;
        max-width: 1190px;
        left:0;
        right:0;
        margin:0 auto;
        // padding: 1rem;
        background: white;
        @media screen and (min-width:992px){
          max-height:500px;
          max-width:800px
        }
        @media screen and (max-width: 1200px) {
          max-height: unset;
        }
        @media screen and (max-width: 767px) {
          max-width: 100%!important;
          padding: 0;
        }
      }
    }
    .cp-utilities-generic_modal-suggestion-text{
      font-size: 17px;
      display: flex;
      justify-content: center;
      font-weight: bold;
      padding-bottom: 1rem;
      text-align: center;
    }
  }
  &-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
    z-index: 1001;
    transition: $transitional-profile-1;

    &-static {
      width: 100%;
      height: 100%;
      opacity: 0.23;
    }

    &-close {
      background: transparent;
      border-radius: 50%;
      border: none;
      position: absolute;
      top: 1rem;
      right: 1rem;
      cursor: pointer;
      color: #ef1b1b;
      z-index: 9999;
      transition: $transitional-profile-1;
      &.in {
        @media screen and (max-width: 767px) {
          top: 0;
          right: 0;
        }
      }
      span {
        i {
          font-weight: 900;
        }
      }
    }
  }
  &-body {
    display: grid;
    height: 100%;
    transition: all 0.5s ease;
    border:1px solid rgba(0,0,0,0.2);
    border-top:0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px
  }
  &-header {
    background:#fff;
    position: relative;
  }
}
.after_add_to_cart_modal {
  .cp-utilities-generic_modal {
    padding: 53px 42px!important;
    background: #E9E9E9!important;
    display: block!important;
    @media screen and (max-width: 1199px) {
      grid-auto-columns: 100%;
    }
    &-overlay-close {
      top: 60px;
      right: 48px;
      background: transparent;
    }
    &-overlay {
      background-color: rgb(34 33 33 / 40%) !important;
    }
    &-body {
      display: block;
      height: auto;
    }
    @media screen and (max-width: 991px) {
      max-height: 615px!important;
      padding: 43px 25px!important;
      &-overlay-close {
        top: 5px;
        right: 21px;
      }
    }
  }
}

.page-header.navbar.navbar-fixed-top{
  &.open-service-modal{
    height:100vh
  }
}
</style>
